import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Badge, Button, Card, Checkbox, ErrorBanner, Field, Modal, Select, cn } from "./ui.js";
import {
  analyzeImportCsv,
  downloadCsv,
  importCsv,
  type ImportAnalysis,
  type ImportSummary,
} from "../lib/api.js";

function SummaryView({
  summary,
  dryRun,
  duplicates,
}: {
  summary: ImportSummary;
  dryRun: boolean;
  duplicates: "skip" | "append";
}) {
  return (
    <div className="space-y-2 rounded-md border border-slate-800 bg-slate-900/60 p-3 text-sm">
      <div className="flex items-center gap-2">
        <Badge tone={dryRun ? "amber" : "green"}>{dryRun ? "Preview" : "Imported"}</Badge>
        {dryRun ? (
          <span className="text-slate-400">No changes were written.</span>
        ) : (
          <span className="text-slate-300">{summary.inserted} item(s) created.</span>
        )}
      </div>
      <ul className="space-y-1 text-slate-300">
        <li>Parsed: {summary.parsed}</li>
        <li>
          Duplicates matched: {summary.duplicate}
          {duplicates === "skip" ? " (skipped)" : " (appended)"}
        </li>
        <li>Dropped (no name): {summary.dropped}</li>
        {summary.details > 0 ? <li>Funko detail rows written: {summary.details}</li> : null}
        {summary.replaced > 0 ? <li>Soft-deleted by replace: {summary.replaced}</li> : null}
      </ul>
      {summary.unmatchedHeaders.length > 0 ? (
        <p className="text-amber-300">
          Ignored columns: {summary.unmatchedHeaders.join(", ")}
        </p>
      ) : null}
      {summary.errors.length > 0 ? (
        <div className="text-amber-300">
          <p className="font-medium">{summary.errors.length} row error(s):</p>
          <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-xs">
            {summary.errors.slice(0, 20).map((error) => (
              <li key={error.row}>
                Row {error.row}: {error.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/** Three-step CSV import: choose file, map columns, confirm. */
function ImportPanel() {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  const [preset, setPreset] = useState("auto");
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<"append" | "replace">("append");
  const [duplicates, setDuplicates] = useState<"skip" | "append">("skip");
  const [lastPreview, setLastPreview] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  function chooseFile(next: File | null) {
    setFile(next);
    setAnalysis(null);
    setSummary(null);
    setError(null);
    setStep(1);
  }

  async function analyze() {
    if (!file) return;
    setError(null);
    setBusy("analyze");
    try {
      const result = await analyzeImportCsv(file);
      setAnalysis(result);
      setMapping(result.mappings[preset] ?? result.mappings.auto ?? {});
      setStep(2);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read the CSV.");
    } finally {
      setBusy(null);
    }
  }

  function applyPreset(id: string) {
    setPreset(id);
    if (analysis) setMapping(analysis.mappings[id] ?? {});
  }

  function setFieldMapping(field: string, column: string) {
    setMapping((previous) => ({ ...previous, [field]: column }));
  }

  async function runImport(asDryRun: boolean) {
    if (!file) {
      setError("Choose a CSV file first.");
      return;
    }
    setError(null);
    setSummary(null);
    setLastPreview(asDryRun);
    setBusy(asDryRun ? "preview" : "import");
    try {
      const result = await importCsv(file, { mode, dryRun: asDryRun, duplicates, mapping });
      setSummary(result);
      if (!asDryRun) {
        await queryClient.invalidateQueries({ queryKey: ["items"] });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Import failed.");
    } finally {
      setBusy(null);
    }
  }

  const nameMapped = Boolean(mapping.name);

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-200">Import</h3>
          <p className="text-xs text-slate-400">
            {step === 1
              ? "Step 1 of 3 — choose a CSV file."
              : step === 2
                ? `Step 2 of 3 — map columns (${analysis?.rowCount ?? 0} ${analysis?.rowCount === 1 ? "row" : "rows"}).`
                : "Step 3 of 3 — confirm."}
          </p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3].map((value) => (
            <span
              key={value}
              className={cn(
                "h-1.5 w-8 rounded-full",
                value <= step ? "bg-indigo-500" : "bg-slate-700",
              )}
            />
          ))}
        </div>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      {step === 1 ? (
        <div className="space-y-3">
          <Field label="CSV file" hint="Any CSV. Columns are detected by name and can be remapped next.">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => chooseFile(event.target.files?.[0] ?? null)}
              className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-300 file:mr-3 file:rounded file:border-0 file:bg-slate-800 file:px-2 file:py-1 file:text-slate-200"
            />
          </Field>
          <div className="flex justify-end">
            <Button disabled={!file || busy !== null} onClick={analyze}>
              {busy === "analyze" ? "Reading…" : "Continue"}
            </Button>
          </div>
        </div>
      ) : null}

      {step === 2 && analysis ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Preset" className="w-64">
              <Select value={preset} onChange={(event) => applyPreset(event.target.value)}>
                {analysis.presets.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label}
                  </option>
                ))}
              </Select>
            </Field>
            <p className="pb-1 text-xs text-slate-400">
              Fields left as “Not mapped” fall back to name detection; unmapped columns are ignored.
            </p>
          </div>

          <div className="max-h-72 overflow-y-auto rounded-md border border-slate-800">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-900 text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">CSV column</th>
                </tr>
              </thead>
              <tbody>
                {analysis.fields.map((field) => (
                  <tr key={field.key} className="border-t border-slate-800/60">
                    <td className="px-3 py-1.5 text-slate-300">
                      {field.label}
                      {field.required ? <span className="text-red-400"> *</span> : null}
                    </td>
                    <td className="px-3 py-1.5">
                      <Select
                        aria-label={`Column for ${field.label}`}
                        value={mapping[field.key] ?? ""}
                        onChange={(event) => setFieldMapping(field.key, event.target.value)}
                        className="w-full"
                      >
                        <option value="">— Not mapped —</option>
                        {analysis.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {analysis.unmatchedHeaders.length > 0 ? (
            <p className="text-xs text-amber-300">
              Unrecognised columns (map them above or they will be ignored):{" "}
              {analysis.unmatchedHeaders.join(", ")}
            </p>
          ) : null}
          {!nameMapped ? (
            <p className="text-xs text-amber-300">
              Name is not mapped — every row will be dropped.
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={() => setStep(3)}>Continue</Button>
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="space-y-3">
          <Field label="Mode">
            <Select
              value={mode}
              onChange={(event) => setMode(event.target.value as "append" | "replace")}
            >
              <option value="append">Append — add to existing inventory</option>
              <option value="replace">Replace — soft-delete existing first</option>
            </Select>
          </Field>

          <Field
            label="Duplicates"
            hint="A row matches an existing item by its id, SKU/barcode, or its variant signature — name, series, box #, exclusive, condition, sticker, chase, vault and box condition. That keeps a chase distinct from a common."
          >
            <Select
              value={duplicates}
              onChange={(event) => setDuplicates(event.target.value as "skip" | "append")}
            >
              <option value="skip">Skip — keep the existing item</option>
              <option value="append">Append — add even if it looks like a duplicate</option>
            </Select>
          </Field>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() => runImport(true)}
            >
              {busy === "preview" ? "Checking…" : "Preview (dry run)"}
            </Button>
            <Button disabled={busy !== null} onClick={() => runImport(false)}>
              {busy === "import" ? "Importing…" : "Import now"}
            </Button>
          </div>

          {summary ? (
            <SummaryView summary={summary} dryRun={lastPreview} duplicates={duplicates} />
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function TransferDialog({
  onClose,
  initialTab = "export",
}: {
  onClose: () => void;
  initialTab?: "import" | "export";
}) {
  const [tab, setTab] = useState<"import" | "export">(initialTab);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [includeSold, setIncludeSold] = useState(false);

  async function runExport(path: string, fallback: string, action: string) {
    setError(null);
    setBusy(action);
    try {
      await downloadCsv(path, fallback);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Export failed.");
    } finally {
      setBusy(null);
    }
  }

  const tabs: Array<{ id: "import" | "export"; label: string }> = [
    { id: "export", label: "Export" },
    { id: "import", label: "Import" },
  ];

  return (
    <Modal title="Import & export" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-900/60 p-1">
          {tabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={cn(
                "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition",
                tab === entry.id
                  ? "bg-indigo-600 text-white"
                  : "text-slate-300 hover:bg-slate-800",
              )}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {error ? <ErrorBanner message={error} /> : null}

        {tab === "export" ? (
          <Card className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-200">Export</h3>
              <p className="text-xs text-slate-400">CSV exports restore inventory fields and features. They do not contain image files, staff accounts, messages or a complete database snapshot. Keep a separate database-and-media backup for full recovery.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={busy !== null}
                onClick={() => runExport("/transfer/export/backup", "farnsworth-backup.csv", "backup")}
              >
                {busy === "backup" ? "Preparing…" : "Inventory CSV (previous app format)"}
              </Button>
            </div>

            <div className="space-y-2 border-t border-slate-800 pt-3">
              <Checkbox
                label="Whatnot export: include sold items"
                checked={includeSold}
                onChange={(event) => setIncludeSold(event.target.checked)}
              />
              <p className="text-xs text-slate-400">
                Sold items are excluded by default, since sold stock cannot be re-listed.
              </p>
              <Button
                variant="secondary"
                disabled={busy !== null}
                onClick={() =>
                  runExport(
                    `/transfer/export/whatnot?sold=${includeSold ? "true" : "false"}`,
                    "whatnot-import.csv",
                    "whatnot",
                  )
                }
              >
                {busy === "whatnot" ? "Preparing…" : "Whatnot template CSV"}
              </Button>
            </div>
          </Card>
        ) : (
          <ImportPanel />
        )}

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
